import { useState, type FormEvent } from "react";
import type { Vehicle } from "@deca/shared-types";
import {
  useCounterparties,
  useDeactivateVehicle,
  useDeleteVehicle,
  useMe,
  useReactivateVehicle,
  useSaveVehicle,
  useVehicles,
} from "../api/hooks";
import { DataTable, createColumnHelper } from "../components/DataTable";
import { Button, Chip, ErrorNotice, Field, Input, Modal, Notice, PageHeader, Select } from "../components/ui";
import { IconPlus } from "../components/icons";
import { formatDateTime, formatKg } from "../lib/format";

/** Catálogo de vehículos del tenant (CLAUDE.md Fase 4). */

const column = createColumnHelper<Vehicle>();

const EMPTY = {
  plateTractor: "",
  mmaTractorKg: "",
  plateTrailer: "",
  mmaTrailerKg: "",
  vehicleType: "",
  counterpartyId: "",
};

export function VehiclesPage() {
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<Vehicle | null>(null);
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState({ ...EMPTY });
  // Filtro de estado (en el navegador: el filtro `isActive` de la API no distingue "false")
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "inactive">("all");

  // Confirmación de baja
  const [deactivateTarget, setDeactivateTarget] = useState<Vehicle | null>(null);
  // Confirmación de eliminar
  const [deleteTarget, setDeleteTarget] = useState<Vehicle | null>(null);

  const query = useVehicles(search ? { search } : {});
  const save = useSaveVehicle();
  const counterparties = useCounterparties();
  const deactivate = useDeactivateVehicle();
  const deleteVehicle = useDeleteVehicle();
  const reactivate = useReactivateVehicle();

  const me = useMe();
  const isAdmin = me.data?.role === "ADMIN";

  const openEdit = (item: Vehicle): void => {
    setEditing(item);
    setValues({
      plateTractor: item.plateTractor,
      mmaTractorKg: item.mmaTractorKg === null ? "" : String(item.mmaTractorKg),
      plateTrailer: item.plateTrailer ?? "",
      mmaTrailerKg: item.mmaTrailerKg === null ? "" : String(item.mmaTrailerKg),
      vehicleType: item.vehicleType ?? "",
      counterpartyId: item.counterpartyId ?? "",
    });
    setOpen(true);
  };

  const onSubmit = (event: FormEvent): void => {
    event.preventDefault();
    const payload: Record<string, unknown> = {
      plateTractor: values.plateTractor.trim().toUpperCase(),
    };
    if (values.mmaTractorKg.trim()) payload.mmaTractorKg = Number(values.mmaTractorKg);
    if (values.plateTrailer.trim()) payload.plateTrailer = values.plateTrailer.trim().toUpperCase();
    if (values.mmaTrailerKg.trim()) payload.mmaTrailerKg = Number(values.mmaTrailerKg);
    if (values.vehicleType.trim()) payload.vehicleType = values.vehicleType.trim();
    payload.counterpartyId = values.counterpartyId ? values.counterpartyId : null;

    save.mutate(editing ? { id: editing.id, values: payload } : { values: payload }, {
      onSuccess: () => setOpen(false),
    });
  };

  const vehicles = (query.data?.items ?? []).filter((item) =>
    statusFilter === "all" ? true : statusFilter === "active" ? item.isActive : !item.isActive,
  );

  const counterpartyNameById = new Map(
    (counterparties.data?.items ?? []).map((item) => [item.id, item.name] as const),
  );

  const columns = [
    column.accessor("plateTractor", {
      header: "Matrícula tractora",
      cell: (info) => <span className="ident">{info.getValue()}</span>,
    }),
    column.accessor("plateTrailer", {
      header: "Matrícula remolque",
      cell: (info) => <span className="ident">{info.getValue() ?? "—"}</span>,
    }),
    column.accessor("mmaTractorKg", {
      header: "MMA tractora",
      cell: (info) => <span className="num">{formatKg(info.getValue())}</span>,
    }),
    column.accessor("mmaTrailerKg", {
      header: "MMA remolque",
      cell: (info) => <span className="num">{formatKg(info.getValue())}</span>,
    }),
    column.accessor("vehicleType", {
      header: "Tipo",
      cell: (info) => info.getValue() ?? "—",
    }),
    column.display({
      id: "counterparty",
      header: "Empresa",
      cell: (info) => {
        const id = info.row.original.counterpartyId;
        const name = id ? counterpartyNameById.get(id) : undefined;
        return name ?? <span className="text-ink-400">—</span>;
      },
    }),
    column.accessor("createdAt", {
      header: "Alta",
      cell: (info) => <span className="num">{formatDateTime(info.getValue())}</span>,
    }),
    column.accessor("isActive", {
      header: "Estado",
      cell: (info) => (info.getValue() ? <Chip tone="ok">Activo</Chip> : <Chip tone="neutral">Inactivo</Chip>),
    }),
    column.display({
      id: "actions",
      header: "",
      cell: (info) => (
        <Button onClick={() => openEdit(info.row.original)}>Editar</Button>
      ),
    }),
  ];

  return (
    <>
      <PageHeader
        eyebrow="Catálogos"
        title="Vehículos"
        description="Tractoras y remolques de la empresa, con su masa máxima autorizada. Se pueden elegir al emitir un documento o escribir la matrícula en el momento."
        actions={
          <Button
            variant="primary"
            onClick={() => {
              setEditing(null);
              setValues({ ...EMPTY });
              setOpen(true);
            }}
          >
            <IconPlus className="h-4 w-4" />
            Nuevo vehículo
          </Button>
        }
      />

      <div className="surface mb-4 grid gap-3 p-4 sm:grid-cols-3">
        <Field label="Buscar por matrícula" htmlFor="filtro-vehiculo">
          <Input
            id="filtro-vehiculo"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="1234-ABC"
          />
        </Field>
        <Field label="Estado" htmlFor="filtro-estado-vehiculo">
          <Select
            id="filtro-estado-vehiculo"
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value as "all" | "active" | "inactive")}
          >
            <option value="all">Todos</option>
            <option value="active">Activos</option>
            <option value="inactive">Inactivos</option>
          </Select>
        </Field>
      </div>

      {query.isError ? (
        <ErrorNotice error={query.error} onRetry={() => void query.refetch()} />
      ) : (
        <DataTable
          columns={columns}
          data={vehicles}
          isLoading={query.isLoading}
          emptyMessage={
            statusFilter === "all"
              ? "No hay vehículos en el catálogo todavía."
              : "No hay vehículos con ese estado."
          }
        />
      )}

      {/* Modal: confirmar baja */}
      <Modal
        open={deactivateTarget !== null}
        title="Dar de baja el vehículo"
        onClose={() => setDeactivateTarget(null)}
      >
        <div className="space-y-4">
          <Notice tone="warn">
            La matrícula <strong className="ident">{deactivateTarget?.plateTractor}</strong> pasará a
            estado <strong>Inactivo</strong> y no podrá seleccionarse en nuevos DECAs. Podrás
            recuperarlo cuando quieras desde Editar → Recuperar.
          </Notice>
          {deactivate.isError ? <ErrorNotice error={deactivate.error} /> : null}
          <div className="flex gap-2">
            <Button
              variant="primary"
              disabled={deactivate.isPending}
              onClick={() => {
                if (!deactivateTarget) return;
                deactivate.mutate(deactivateTarget.id, {
                  onSuccess: () => setDeactivateTarget(null),
                });
              }}
            >
              {deactivate.isPending ? "Procesando…" : "Dar de baja"}
            </Button>
            <Button onClick={() => setDeactivateTarget(null)}>Cancelar</Button>
          </div>
        </div>
      </Modal>

      {/* Modal: confirmar eliminación */}
      <Modal
        open={deleteTarget !== null}
        title="Eliminar vehículo"
        onClose={() => setDeleteTarget(null)}
      >
        <div className="space-y-4">
          <Notice tone="danger">
            Se eliminará permanentemente el vehículo{" "}
            <strong className="ident">{deleteTarget?.plateTractor}</strong>. Solo es posible si no
            tiene grupos de transporte asociados. Esta acción <strong>no se puede deshacer</strong>.
          </Notice>
          {deleteVehicle.isError ? <ErrorNotice error={deleteVehicle.error} /> : null}
          <div className="flex gap-2">
            <Button
              variant="primary"
              disabled={deleteVehicle.isPending}
              onClick={() => {
                if (!deleteTarget) return;
                deleteVehicle.mutate(deleteTarget.id, {
                  onSuccess: () => setDeleteTarget(null),
                });
              }}
            >
              {deleteVehicle.isPending ? "Eliminando…" : "Eliminar definitivamente"}
            </Button>
            <Button onClick={() => setDeleteTarget(null)}>Cancelar</Button>
          </div>
        </div>
      </Modal>

      <Modal open={open} title={editing ? "Editar vehículo" : "Nuevo vehículo"} onClose={() => setOpen(false)}>
        <form onSubmit={onSubmit} className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Matrícula tractora" htmlFor="v-tractor">
              <Input
                id="v-tractor"
                required
                value={values.plateTractor}
                onChange={(event) => setValues({ ...values, plateTractor: event.target.value })}
                placeholder="1234-ABC"
              />
            </Field>
            <Field label="MMA tractora (kg)" htmlFor="v-mma-tractor">
              <Input
                id="v-mma-tractor"
                inputMode="numeric"
                value={values.mmaTractorKg}
                onChange={(event) => setValues({ ...values, mmaTractorKg: event.target.value })}
              />
            </Field>
            <Field label="Matrícula remolque" htmlFor="v-remolque">
              <Input
                id="v-remolque"
                value={values.plateTrailer}
                onChange={(event) => setValues({ ...values, plateTrailer: event.target.value })}
                placeholder="R-5678-BCD"
              />
            </Field>
            <Field label="MMA remolque (kg)" htmlFor="v-mma-remolque">
              <Input
                id="v-mma-remolque"
                inputMode="numeric"
                value={values.mmaTrailerKg}
                onChange={(event) => setValues({ ...values, mmaTrailerKg: event.target.value })}
              />
            </Field>
            <div className="sm:col-span-2">
              <Field label="Tipo de vehículo" htmlFor="v-tipo" hint='Ej.: "rígido", "tractora + semirremolque".'>
                <Input
                  id="v-tipo"
                  value={values.vehicleType}
                  onChange={(event) => setValues({ ...values, vehicleType: event.target.value })}
                />
              </Field>
            </div>
            <div className="sm:col-span-2">
              <Field label="Empresa (opcional)" htmlFor="v-empresa" hint="El vehículo puede pertenecer a una empresa/cargador concreto del tenant.">
                <Select
                  id="v-empresa"
                  value={values.counterpartyId}
                  onChange={(event) => setValues({ ...values, counterpartyId: event.target.value })}
                >
                  <option value="">— Sin asignar —</option>
                  {(counterparties.data?.items ?? [])
                    .filter((item) => item.isActive || item.id === values.counterpartyId)
                    .map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
          </div>

          {save.isError ? <ErrorNotice error={save.error} /> : null}

          <div className="flex gap-2">
            <Button type="submit" variant="primary" disabled={save.isPending}>
              {save.isPending ? "Guardando…" : "Guardar"}
            </Button>
            <Button onClick={() => setOpen(false)}>Cancelar</Button>
          </div>

          {isAdmin && editing ? (
            <>
              <hr className="border-ink-100" />
              <div className="space-y-2">
                <p className="text-sm font-medium text-ink-500">Acciones de administrador</p>
                <div className="flex gap-2">
                  {editing.isActive ? (
                    <Button
                      variant="quiet"
                      onClick={() => {
                        setOpen(false);
                        setDeactivateTarget(editing);
                      }}
                    >
                      Dar de baja
                    </Button>
                  ) : (
                    <Button
                      variant="quiet"
                      disabled={reactivate.isPending}
                      onClick={() =>
                        reactivate.mutate(editing.id, { onSuccess: () => setOpen(false) })
                      }
                    >
                      {reactivate.isPending ? "Recuperando…" : "Recuperar"}
                    </Button>
                  )}
                  <Button
                    variant="quiet"
                    onClick={() => {
                      setOpen(false);
                      setDeleteTarget(editing);
                    }}
                  >
                    Eliminar
                  </Button>
                </div>
                {reactivate.isError ? <ErrorNotice error={reactivate.error} /> : null}
              </div>
            </>
          ) : null}
        </form>
      </Modal>
    </>
  );
}
